import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {CopilotAppOpenAction, CopilotAppOpenActionMenuItem} from '@github-ui/copilot-app-launcher-shared'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {McpInstallButton, type McpInstallTarget} from '@github-ui/mcp-install-button'
import {LibraryIcon, MarkGithubIcon, TerminalIcon, TriangleDownIcon, type Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, Button, Link} from '@primer/react'
import {createContext, use, useCallback, useId, useState} from 'react'

import styles from './AIResourceResults.module.css'
import type {AIResourcePluginMarketplace} from './types'

export type AIResourceInstallAction =
  | {type: 'deeplink'; command: string; target: string}
  | {type: 'mcp_server'; target: McpInstallTarget; command: string}
  | {type: 'skill'; command: string; hasCompanionFiles: boolean}
  | {type: 'plugin'; command: string; marketplaces: readonly AIResourcePluginMarketplace[]}

export type AIResourceResult = {
  id: string
  /** An authorized canonical destination supplied by the composing application. */
  href: string
  name: string
  iconUrl?: string
  fallbackIconUrl?: string
  description?: string
  resourceType: string
  resourceTypeIcon?: Icon
  publisher?: {
    name: string
    /** An authorized canonical destination supplied by the composing application. */
    href: string
  }
  labels: readonly string[]
  installAction?: AIResourceInstallAction
  connectorIds?: readonly string[]
}

export type AIResourceResultsProps = {
  results: readonly AIResourceResult[]
}

export function AIResourceResults({results}: AIResourceResultsProps) {
  if (results.length === 0) return null

  return (
    <ListView itemsListClassName={styles.List} title="AI resources" totalCount={results.length}>
      {results.map(result => (
        <AIResourceResultRow key={result.id} result={result} />
      ))}
    </ListView>
  )
}

export function AIResourceResultRow({result}: {result: AIResourceResult}) {
  const ResourceTypeIcon = result.resourceTypeIcon
  const firstConnectorId = result.connectorIds?.[0]
  const canViewConnector = isFeatureEnabled('agentfinder_connectors_ghapp_deeplink') && firstConnectorId !== undefined
  const connectorInstallId =
    canViewConnector && isFeatureEnabled('agentfinder_connectors_ghapp_install') ? firstConnectorId : undefined

  return (
    <ListItem
      className={styles.Row}
      title={
        <ListItemTitle
          href={result.href}
          value={
            <span className={styles.Title}>
              <ResourceIcon key={result.iconUrl} url={result.iconUrl} fallbackUrl={result.fallbackIconUrl} />
              <span className={styles.TitleText}>{result.name}</span>
            </span>
          }
          linkProps={{as: Link, className: styles.Link}}
        />
      }
      metadata={
        result.installAction || canViewConnector ? (
          <ConnectorInstallContext value={connectorInstallId}>
            <InstallAction action={result.installAction} name={result.name} canViewConnector={canViewConnector} />
          </ConnectorInstallContext>
        ) : undefined
      }
      metadataContainerClassName={styles.InstallMetadata}
    >
      <ListItemMainContent>
        <div className={styles.Content}>
          {result.description && (
            <ListItemDescription>
              <ListItemDescriptionItem>{result.description}</ListItemDescriptionItem>
            </ListItemDescription>
          )}

          <ListItemDescription>
            <span className={styles.Metadata}>
              <span className={styles.ResourceType}>
                {ResourceTypeIcon && <ResourceTypeIcon aria-hidden="true" data-testid="resource-type-icon" />}
                <span>{result.resourceType}</span>
              </span>
              {result.publisher && (
                <>
                  <span className={styles.Separator} aria-hidden="true" />
                  <Link className={styles.Link} href={result.publisher.href}>
                    {result.publisher.name}
                  </Link>
                </>
              )}
            </span>
          </ListItemDescription>
        </div>
      </ListItemMainContent>
    </ListItem>
  )
}

function ResourceIcon({url, fallbackUrl}: {url?: string; fallbackUrl?: string}) {
  const [failed, setFailed] = useState(false)
  const checkLoadedImage = useCallback((image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth === 0) setFailed(true)
  }, [])
  if (!url || !isSafeIconUrl(url) || failed) {
    if (fallbackUrl) return <ResourceIcon key={fallbackUrl} url={fallbackUrl} />
    return (
      <span className={`${styles.Icon} ${styles.FallbackIcon}`} aria-hidden="true" data-testid="resource-icon-fallback">
        <LibraryIcon size={16} data-testid="resource-icon-fallback-glyph" />
      </span>
    )
  }

  return (
    <img
      ref={checkLoadedImage}
      className={styles.Icon}
      src={url}
      alt=""
      width={20}
      height={20}
      referrerPolicy="no-referrer"
      data-testid="resource-icon"
      onError={() => setFailed(true)}
    />
  )
}

function isSafeIconUrl(value: string): boolean {
  try {
    // eslint-disable-next-line no-restricted-syntax -- Only absolute image URLs are valid, including during SSR.
    const url = new URL(value)
    const isLocalProxy =
      process.env.NODE_ENV === 'development' &&
      url.protocol === 'http:' &&
      url.hostname === 'github.localhost' &&
      url.port === '8081'
    return (
      !url.username &&
      !url.password &&
      (isLocalProxy ||
        (url.protocol === 'https:' &&
          !url.port &&
          (url.hostname.endsWith('.githubusercontent.com') || url.hostname.endsWith('.githubassets.com'))))
    )
  } catch (error) {
    if (error instanceof TypeError) return false
    throw error
  }
}

const ConnectorInstallContext = createContext<string | undefined>(undefined)

function GitHubAppConnectorAction({inMenu = true}: {inMenu?: boolean}) {
  const connectorInstallId = use(ConnectorInstallContext)

  if (connectorInstallId !== undefined) {
    const target = `ghapp://air/install?connectorId=${encodeURIComponent(connectorInstallId)}`
    if (inMenu) {
      return (
        <CopilotAppOpenActionMenuItem
          open={target}
          entryPoint="dotcom_ai-resource-search_install"
          // eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- GitHub App is the product name.
          label="Install in GitHub App"
        />
      )
    }
    return (
      <Button
        as="a"
        alignContent="start"
        block
        href={`/copilot/app/launch?open=${encodeURIComponent(target)}&entry_point=dotcom_ai-resource-search_install`}
        leadingVisual={MarkGithubIcon}
      >
        {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- GitHub App is the product name. */}
        {'Install in GitHub App'}
      </Button>
    )
  }

  const AppAction = inMenu ? CopilotAppOpenActionMenuItem : CopilotAppOpenAction
  return (
    <AppAction
      open="ghapp://extensions/connectors"
      entryPoint="dotcom_ai-resource-search_connectors"
      // eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- GitHub App is the product name.
      label="View in GitHub App"
    />
  )
}

function GitHubAppConnectorDescription() {
  const installsConnector = use(ConnectorInstallContext) !== undefined

  return (
    <p className={styles.SkillInstallDescription}>
      {installsConnector
        ? 'Install this connector directly in GitHub App.'
        : 'Browse available connectors in GitHub App.'}
    </p>
  )
}

function GitHubAppConnectorSection() {
  const headingId = useId()
  const installsConnector = use(ConnectorInstallContext) !== undefined

  return (
    <section className={styles.ConnectorSection} aria-labelledby={headingId}>
      <h4 className={styles.SkillInstallHeading} id={headingId}>
        <MarkGithubIcon aria-hidden="true" />
        {}
        <span>GitHub App</span>
      </h4>
      <GitHubAppConnectorDescription />
      {installsConnector ? (
        <GitHubAppConnectorAction inMenu={false} />
      ) : (
        <ActionList>
          <GitHubAppConnectorAction inMenu={false} />
        </ActionList>
      )}
    </section>
  )
}

function InstallAction({
  action,
  name,
  canViewConnector,
}: {
  action?: AIResourceInstallAction
  name: string
  canViewConnector: boolean
}) {
  if (action?.type === 'mcp_server') {
    return <McpInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  if (action?.type === 'deeplink') {
    return <DeeplinkInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  if (action) {
    return <CLIInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  return (
    <ActionMenu>
      <ActionMenu.Button size="small" aria-label={`Install ${name}`}>
        Install
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <GitHubAppConnectorAction />
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function DeeplinkInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Extract<AIResourceInstallAction, {type: 'deeplink'}>
  name: string
  canViewConnector: boolean
}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()
  const cliHeadingId = useId()
  const installsConnector = use(ConnectorInstallContext) !== undefined

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      overlayProps={{
        'aria-labelledby': headingId,
        'aria-modal': true,
        role: 'dialog',
      }}
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          aria-haspopup="dialog"
          aria-label={`Install ${name}`}
          size="small"
          trailingVisual={TriangleDownIcon}
        >
          Install
        </Button>
      )}
      width="large"
    >
      <section className={styles.InstallPanelSection} aria-labelledby={headingId}>
        <h4 className={styles.SkillInstallHeading} id={headingId}>
          <MarkGithubIcon aria-hidden="true" />
          <span>GitHub App</span>
        </h4>
        {canViewConnector && <GitHubAppConnectorDescription />}
        {canViewConnector ? (
          installsConnector ? (
            <GitHubAppConnectorAction inMenu={false} />
          ) : (
            <ActionList>
              <GitHubAppConnectorAction inMenu={false} />
            </ActionList>
          )
        ) : (
          <Button
            as="a"
            alignContent="start"
            block
            href={`/copilot/app/launch?open=${encodeURIComponent(action.target)}&entry_point=dotcom_ai-resource-search_install`}
            leadingVisual={MarkGithubIcon}
          >
            {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- GitHub App is the product name. */}
            {'Install in GitHub App'}
          </Button>
        )}
      </section>
      <hr className={styles.InstallPanelDivider} />
      <section className={styles.InstallPanelSection} aria-labelledby={cliHeadingId}>
        <h4 className={styles.SkillInstallHeading} id={cliHeadingId}>
          <TerminalIcon aria-hidden="true" data-testid="deeplink-install-terminal-icon" />
          <span>Copilot CLI</span>
        </h4>
        <CLIInstallOption
          title="Install with Copilot CLI"
          description="Run this command in your terminal. Copilot will verify the resource and ask before installing it."
          command={action.command}
        />
      </section>
    </AnchoredOverlay>
  )
}

function McpInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Extract<AIResourceInstallAction, {type: 'mcp_server'}>
  name: string
  canViewConnector: boolean
}) {
  return (
    <McpInstallButton
      mcp={action.target}
      muted
      layout="panel"
      ariaLabel={`Install ${name}`}
      analyticsResourceName={name}
      copilotAppEntryPoint="dotcom_ai-resource-search_install"
      footer={
        <>
          <h4 className={styles.SkillInstallHeading}>
            <TerminalIcon aria-hidden="true" data-testid="mcp-install-terminal-icon" />
            <span>{canViewConnector ? 'Install options' : 'Copilot CLI'}</span>
          </h4>
          <CLIInstallOption
            title="Find MCP server"
            description="Select the server from the search results and complete any required configuration. It may not appear if your CLI uses an organization-configured registry."
            command={action.command}
          />
          {canViewConnector && <GitHubAppConnectorSection />}
        </>
      }
    />
  )
}

function CLIInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Exclude<AIResourceInstallAction, {type: 'mcp_server'} | {type: 'deeplink'}>
  name: string
  canViewConnector: boolean
}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      overlayProps={{
        'aria-labelledby': headingId,
        'aria-modal': true,
        role: 'dialog',
      }}
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          aria-haspopup="dialog"
          aria-label={`Install ${name}`}
          size="small"
          trailingVisual={TriangleDownIcon}
        >
          Install
        </Button>
      )}
      width="large"
    >
      <div className={styles.SkillInstallMenu}>
        <h4 className={styles.SkillInstallHeading} id={headingId}>
          <TerminalIcon aria-hidden="true" data-testid="skill-install-terminal-icon" />
          <span>{canViewConnector ? 'Install options' : 'Copilot CLI'}</span>
        </h4>
        {action.type === 'skill' ? (
          <CLIInstallOption
            title={action.hasCompanionFiles ? 'Install skill with supporting files' : 'Install skill'}
            description={
              action.hasCompanionFiles
                ? 'Clone the repository to include the supporting files this skill requires. Keep the clone in place.'
                : 'This skill doesn’t require any supporting files.'
            }
            command={action.command}
            multiline={action.hasCompanionFiles}
          />
        ) : (
          <div className={styles.CLIInstallOptions}>
            {action.marketplaces.map(marketplace => (
              <section
                className={styles.SkillInstallOption}
                key={`${marketplace.source}:${marketplace.name}:${marketplace.entry}`}
              >
                <h5 className={styles.SkillInstallOptionHeading}>Install via {marketplace.name} marketplace</h5>
                <p className={styles.SkillInstallDescription}>Run these commands in order in your terminal.</p>
                <div className={styles.PluginMarketplaceSteps}>
                  <div className={styles.PluginMarketplaceStep}>
                    <p className={styles.SkillInstallDescription}>First, add the marketplace.</p>
                    <CLIInstallCommand
                      ariaLabel={`Add ${marketplace.name} marketplace command`}
                      command={`copilot plugin marketplace add ${marketplace.source}`}
                      copyLabel={`Copy ${marketplace.name} marketplace add command to clipboard`}
                    />
                  </div>
                  <div className={styles.PluginMarketplaceStep}>
                    <p className={styles.SkillInstallDescription}>Then, install the verified marketplace entry.</p>
                    <CLIInstallCommand
                      ariaLabel={`Install ${marketplace.entry} from ${marketplace.name} command`}
                      command={`copilot plugin install ${marketplace.entry}@${marketplace.name}`}
                      copyLabel={`Copy ${marketplace.entry} from ${marketplace.name} install command to clipboard`}
                    />
                  </div>
                </div>
              </section>
            ))}
            <CLIInstallOption
              title={action.marketplaces.length > 0 ? 'Or install directly' : 'Install plugin from repository'}
              description="Run this command in your terminal to install the verified plugin directory."
              command={action.command}
              copyLabel="Copy repository install command to clipboard"
            />
          </div>
        )}
        {canViewConnector && <GitHubAppConnectorSection />}
      </div>
    </AnchoredOverlay>
  )
}

function CLIInstallOption({
  title,
  description,
  command,
  multiline = false,
  copyLabel = 'Copy command to clipboard',
}: {
  title: string
  description: string
  command: string
  multiline?: boolean
  copyLabel?: string
}) {
  return (
    <section className={styles.SkillInstallOption}>
      <h5 className={styles.SkillInstallOptionHeading}>{title}</h5>
      <p className={styles.SkillInstallDescription}>{description}</p>
      <CLIInstallCommand ariaLabel={`${title} command`} command={command} copyLabel={copyLabel} multiline={multiline} />
    </section>
  )
}

function CLIInstallCommand({
  ariaLabel,
  command,
  copyLabel,
  multiline = false,
}: {
  ariaLabel: string
  command: string
  copyLabel: string
  multiline?: boolean
}) {
  return (
    <div className={styles.SkillInstallCommand}>
      <pre
        aria-label={ariaLabel}
        className={`${styles.SkillInstallCode} ${multiline ? styles.SkillInstallMultilineCode : ''}`}
        role="region"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard-scrollable overflow region
        tabIndex={0}
      >
        <code>{command}</code>
      </pre>
      <CopyToClipboardIconButton
        className={styles.SkillInstallCopyButton}
        textToCopy={command}
        ariaLabel={copyLabel}
        tooltipProps={{direction: 'nw'}}
      />
    </div>
  )
}
